import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Loader2, Download, ExternalLink, X, ZoomIn, ZoomOut, RotateCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * PDF Viewer Modal Component
 * Displays a PDF file in a modal dialog with zoom and download options
 * 
 * @param {boolean} open - Whether the modal is open
 * @param {function} onClose - Callback to close the modal
 * @param {string} pdfUrl - Direct URL to the PDF file (optional if using code)
 * @param {string} code - Material code to fetch PDF by code (optional if using pdfUrl)
 * @param {string} filename - Filename to display in the header
 * @param {string} title - Title to display in the header
 */
export function PdfViewerModal({ open, onClose, pdfUrl, code, filename, title }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [blobUrl, setBlobUrl] = useState(null);
  const [zoom, setZoom] = useState(100);
  
  useEffect(() => {
    if (open && (pdfUrl || code)) {
      loadPdf();
    }
    
    return () => {
      // Cleanup blob URL when closing
      if (blobUrl) {
        window.URL.revokeObjectURL(blobUrl);
        setBlobUrl(null);
      }
    };
  }, [open, pdfUrl, code]);
  
  const loadPdf = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      const url = pdfUrl || `${API_URL}/api/drawings/view-by-code/${encodeURIComponent(code)}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Erro ao carregar PDF');
      }
      
      const blob = await response.blob();
      const newBlobUrl = window.URL.createObjectURL(blob);
      setBlobUrl(newBlobUrl);
    } catch (err) {
      setError(err.message || 'Erro ao carregar PDF');
    } finally {
      setLoading(false);
    }
  };
  
  const handleDownload = () => {
    if (blobUrl) {
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename || `${code || 'desenho'}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
    }
  };
  
  const handleOpenNewTab = () => {
    if (blobUrl) {
      window.open(blobUrl, '_blank');
    }
  };
  
  const handleClose = () => {
    if (blobUrl) {
      window.URL.revokeObjectURL(blobUrl);
      setBlobUrl(null);
    }
    setZoom(100);
    setError(null);
    onClose();
  };
  
  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 25, 200));
  };
  
  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 25, 50));
  };
  
  const handleResetZoom = () => {
    setZoom(100);
  };
  
  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-6xl w-[95vw] h-[90vh] flex flex-col p-0" data-testid="pdf-viewer-modal">
        <DialogHeader className="px-4 py-3 border-b border-slate-200 flex flex-row items-center justify-between">
          <div className="flex items-center gap-3">
            <DialogTitle className="text-lg font-semibold text-slate-900">
              {title || filename || code || 'Visualizar PDF'}
            </DialogTitle>
            {filename && title && (
              <span className="text-sm text-slate-500 font-mono bg-slate-100 px-2 py-0.5 rounded">
                {filename}
              </span>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            {/* Zoom controls */}
            <div className="flex items-center gap-1 border-r border-slate-200 pr-2 mr-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleZoomOut}
                disabled={zoom <= 50}
                className="h-8 w-8 p-0"
                title="Diminuir zoom"
              >
                <ZoomOut className="h-4 w-4" />
              </Button>
              <span className="text-sm text-slate-600 w-12 text-center">{zoom}%</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleZoomIn}
                disabled={zoom >= 200}
                className="h-8 w-8 p-0"
                title="Aumentar zoom"
              >
                <ZoomIn className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleResetZoom}
                className="h-8 w-8 p-0"
                title="Resetar zoom"
              >
                <RotateCw className="h-4 w-4" />
              </Button>
            </div>
            
            {/* Action buttons */}
            <Button
              variant="outline"
              size="sm"
              onClick={handleOpenNewTab}
              disabled={!blobUrl}
              className="h-8"
              title="Abrir em nova aba"
            >
              <ExternalLink className="h-4 w-4 mr-1" />
              Nova Aba
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownload}
              disabled={!blobUrl}
              className="h-8"
              title="Baixar arquivo"
            >
              <Download className="h-4 w-4 mr-1" />
              Baixar
            </Button>
          </div>
        </DialogHeader>
        
        <div className="flex-1 overflow-auto bg-slate-100 p-4">
          {loading ? (
            <div className="flex flex-col items-center justify-center h-full text-slate-500">
              <Loader2 className="h-10 w-10 animate-spin mb-3 text-blue-500" />
              <p>Carregando PDF...</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-full text-red-500">
              <X className="h-10 w-10 mb-3" />
              <p className="font-medium">Erro ao carregar PDF</p>
              <p className="text-sm text-slate-500 mt-1">{error}</p>
              <Button
                variant="outline"
                size="sm"
                onClick={loadPdf}
                className="mt-4"
              >
                Tentar novamente
              </Button>
            </div>
          ) : blobUrl ? (
            <div 
              className="flex justify-center"
              style={{ transform: `scale(${zoom / 100})`, transformOrigin: 'top center' }}
            >
              <iframe
                src={blobUrl}
                className="w-full bg-white shadow-lg rounded-lg"
                style={{ 
                  height: `${Math.max(70, 70 * (100 / zoom))}vh`,
                  minWidth: '800px',
                  maxWidth: '100%'
                }}
                title="PDF Viewer"
                data-testid="pdf-iframe"
              />
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-slate-400">
              <p>Nenhum PDF selecionado</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default PdfViewerModal;
